import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Upload, Mic, Loader2, Check, Trash2, AlertTriangle, ArrowRight, Lightbulb, Play, Pause, RotateCcw, Volume2, Lock } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { voiceover } from '../services/api';
import { trackAudioEvent } from '../utils/telemetry';
import {
    trackPremiumAdoptionEvent,
    hasRecordedFirstAction,
    markFirstAction,
} from '../utils/telemetry';
import UpgradeNudge from './UpgradeNudge';

/**
 * VoiceSetupModal — Teacher voice profile creation/management.
 *
 * States:
 *  - loading: checking for existing profile
 *  - no-voice: upload/record form + consent
 *  - creating: phased cloning progress
 *  - preview: hear the cloned voice before committing (non-blocking)
 *  - has-voice: profile exists, show details + delete option
 */

const CREATION_PHASES = [
    'Uploading your voice sample…',
    'Analyzing your voice…',
    'Creating your teaching voice…',
];

// Fallback presets — replaced by dynamic loading from /api/voices/presets
const FALLBACK_PRESETS = [
    { id: 'balanced', label: 'Clear Instruction', description: 'Clear, steady classroom delivery', best_for: 'Everyday lessons and direct instruction', available: true },
    { id: 'more_stable', label: 'Warm Narration', description: 'Warm, calm read-aloud tone', best_for: 'Read-alouds, story time, calm introductions', available: true },
    { id: 'more_like_me', label: 'Engaging Explanation', description: 'Expressive, closer to your natural voice', best_for: 'Science demos, math walkthroughs, energetic lessons', available: true },
];

const VoiceSetupModal = ({ isOpen, onClose, teacherId, teacherName, onVoiceCreated }) => {
    const [loading, setLoading] = useState(true);
    const [voiceProfile, setVoiceProfile] = useState(null);
    const [audioFile, setAudioFile] = useState(null);
    const [fileDuration, setFileDuration] = useState(null); // estimated sample duration
    const [displayName, setDisplayName] = useState('');
    const [consentChecked, setConsentChecked] = useState(false);
    const [creating, setCreating] = useState(false);
    const [creationPhase, setCreationPhase] = useState(0);
    const [deleting, setDeleting] = useState(false);
    const [confirmingDelete, setConfirmingDelete] = useState(false);
    const [error, setError] = useState(null);
    const phaseTimerRef = useRef(null);

    // Preview state
    const [showPreview, setShowPreview] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [previewError, setPreviewError] = useState(null);
    const [previewPlaying, setPreviewPlaying] = useState(false);
    const previewAudioRef = useRef(null);

    // Preset state
    const [activePreset, setActivePreset] = useState('balanced');
    const [presetUpdating, setPresetUpdating] = useState(false);
    const [availablePresets, setAvailablePresets] = useState(FALLBACK_PRESETS);

    // Source sample state (for comparison)
    const [sourceSampleUrl, setSourceSampleUrl] = useState(null);
    const [sourcePlaying, setSourcePlaying] = useState(false);
    const sourceAudioRef = useRef(null);

    // Clean up phase timer on unmount/close
    const clearPhaseTimer = useCallback(() => {
        if (phaseTimerRef.current) {
            clearInterval(phaseTimerRef.current);
            phaseTimerRef.current = null;
        }
    }, []);

    useEffect(() => {
        if (!isOpen) {
            clearPhaseTimer();
            setShowPreview(false);
            setPreviewUrl(null);
            setPreviewError(null);
            setPreviewPlaying(false);
            setSourcePlaying(false);
            setCreationPhase(0);
            setCreating(false);
            if (previewAudioRef.current) previewAudioRef.current.pause();
            if (sourceAudioRef.current) sourceAudioRef.current.pause();
        }
    }, [isOpen, clearPhaseTimer]);

    useEffect(() => () => {
        clearPhaseTimer();
        if (previewUrl) URL.revokeObjectURL(previewUrl);
    }, [clearPhaseTimer, previewUrl]);

    // Fetch existing voice profile + source sample on open
    useEffect(() => {
        if (isOpen && teacherId) {
            setLoading(true);
            setError(null);
            trackAudioEvent('audio_voice_setup_opened');

            voiceover.getMyVoice(teacherId)
                .then(res => {
                    const profile = res.data?.voice_profile || null;
                    setVoiceProfile(profile);
                    if (profile) {
                        setActivePreset(profile.voice_preset || 'balanced');
                    }
                })
                .catch(err => {
                    console.error('Failed to fetch voice profile:', err);
                    setVoiceProfile(null);
                })
                .finally(() => setLoading(false));

            // Fetch source sample URL (non-blocking)
            voiceover.getSourceSample(teacherId)
                .then(res => {
                    if (res.data?.source_sample_url) {
                        setSourceSampleUrl(res.data.source_sample_url);
                    }
                })
                .catch(() => {}); // Non-blocking

            // Load plan-aware presets (non-blocking)
            voiceover.getVoicePresets(teacherId)
                .then(res => {
                    if (res.data?.presets?.length) {
                        setAvailablePresets(res.data.presets);
                    }
                })
                .catch(() => {}); // Non-blocking — fallback presets used
        }
    }, [isOpen, teacherId]);

    // Estimate file duration using Audio API
    const estimateDuration = useCallback((file) => {
        const url = URL.createObjectURL(file);
        const audio = new Audio(url);
        audio.addEventListener('loadedmetadata', () => {
            if (audio.duration && isFinite(audio.duration)) {
                setFileDuration(Math.round(audio.duration));
            } else {
                // Fallback: estimate from file size (~16KB/s for MP3 128kbps)
                setFileDuration(Math.round(file.size / 16000));
            }
            URL.revokeObjectURL(url);
        });
        audio.addEventListener('error', () => {
            // Fallback estimate
            setFileDuration(Math.round(file.size / 16000));
            URL.revokeObjectURL(url);
        });
    }, []);

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            setAudioFile(file);
            setFileDuration(null);
            estimateDuration(file);
        }
    };

    // Intelligent default for voice name
    const getDefaultDisplayName = useCallback(() => {
        if (teacherName) {
            return `${teacherName.split(' ')[0]}'s Teaching Voice`;
        }
        return 'My Teaching Voice';
    }, [teacherName]);

    // Generate preview audio (non-blocking)
    const generatePreview = useCallback(async () => {
        setPreviewLoading(true);
        setPreviewError(null);
        if (previewAudioRef.current) previewAudioRef.current.pause();
        setPreviewPlaying(false);
        // Revoke previous preview URL to free memory
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
        try {
            const res = await voiceover.previewVoice({
                teacher_id: teacherId,
            });
            const blob = new Blob([res.data], { type: 'audio/mpeg' });
            const url = URL.createObjectURL(blob);
            setPreviewUrl(url);
            trackAudioEvent('audio_preview_generated', { preset: activePreset });
        } catch (err) {
            console.warn('Preview generation failed (non-blocking):', err);
            setPreviewError('We created your voice, but couldn\'t generate the preview right now.');
            trackAudioEvent('audio_preview_failed');
        } finally {
            setPreviewLoading(false);
        }
    }, [teacherId, previewUrl, activePreset]);

    // Change preset, save to server, re-generate preview
    const handlePresetChange = useCallback(async (presetId) => {
        if (presetId === activePreset || presetUpdating) return;
        // Check if preset is available for this plan
        const preset = availablePresets.find(p => p.id === presetId);
        if (preset && !preset.available) return;
        setPresetUpdating(true);
        const previousPreset = activePreset;
        setActivePreset(presetId);
        trackAudioEvent('audio_preset_changed', { from_preset: previousPreset, to_preset: presetId });

        // Phase 3B: Track first premium preset use (non-default)
        if (presetId !== 'balanced' && !hasRecordedFirstAction('premium_preset')) {
            markFirstAction('premium_preset');
            trackPremiumAdoptionEvent('first_premium_audio_preset_used', { preset_id: presetId });
        }
        try {
            await voiceover.updateVoicePreset({
                teacher_id: teacherId,
                preset: presetId,
            });
            // Re-generate preview with new preset
            await generatePreview();
        } catch (err) {
            console.warn('Preset update failed:', err);
        } finally {
            setPresetUpdating(false);
        }
    }, [activePreset, presetUpdating, teacherId, generatePreview, availablePresets]);

    const handleCreate = async () => {
        if (!audioFile || !consentChecked) return;

        // Use entered name or smart default
        const finalName = displayName.trim() || getDefaultDisplayName();

        setCreating(true);
        setCreationPhase(0);
        setError(null);

        // Start phased progress labels
        phaseTimerRef.current = setInterval(() => {
            setCreationPhase(prev => Math.min(prev + 1, CREATION_PHASES.length - 1));
        }, 3000);

        try {
            const formData = new FormData();
            formData.append('audio_file', audioFile);
            formData.append('display_name', finalName);
            formData.append('consent_confirmed', 'true');
            formData.append('teacher_id', teacherId);

            const res = await voiceover.cloneVoice(formData);

            if (res.data?.success) {
                clearPhaseTimer();
                const profile = res.data.voice_profile;
                setVoiceProfile(profile);
                setDisplayName(finalName);
                setActivePreset(profile.voice_preset || 'balanced');
                trackAudioEvent('audio_voice_created', {
                    sample_quality: res.data.sample_quality,
                    display_name: finalName,
                });

                // Transition to preview step
                setShowPreview(true);
                setCreating(false);

                // Start preview generation (non-blocking)
                generatePreview();
            }
        } catch (err) {
            console.error('Voice cloning failed:', err);
            clearPhaseTimer();
            const detail = err.response?.data?.detail || '';
            // Teacher-friendly error mapping
            if (detail.includes('too short') || detail.includes('seems too short')) {
                setError(detail); // Backend already sends good message
            } else if (detail.includes('empty or corrupted')) {
                setError(detail);
            } else if (detail.includes('Unsupported audio format')) {
                setError('That file format isn\'t supported. Please use an MP3, WAV, or M4A file.');
            } else if (detail.includes('already exists')) {
                setError('You already have a voice profile. Delete your current one first to create a new one.');
            } else if (detail.includes('consent')) {
                setError('Please confirm the consent checkbox before creating your voice.');
            } else if (detail.includes('temporarily unavailable')) {
                setError('The voice service is temporarily unavailable. Please try again in a few minutes.');
            } else if (detail.includes('busy')) {
                setError('The voice service is busy right now. Please wait a moment and try again.');
            } else if (detail) {
                setError(detail);
            } else {
                setError('Something went wrong creating your voice. Please try again.');
            }
        } finally {
            setCreating(false);
        }
    };

    const handleDelete = async () => {
        setDeleting(true);
        setError(null);

        try {
            await voiceover.deleteMyVoice(teacherId);
            trackAudioEvent('audio_voice_deleted');
            setVoiceProfile(null);
            setAudioFile(null);
            setFileDuration(null);
            setDisplayName('');
            setConsentChecked(false);
            setShowPreview(false);
            setPreviewUrl(null);
            setPreviewError(null);
            setConfirmingDelete(false);
        } catch (err) {
            console.error('Voice deletion failed:', err);
            setError('Couldn\'t remove your voice profile right now. Please try again.');
        } finally {
            setDeleting(false);
        }
    };

    const handleContinue = () => {
        if (previewAudioRef.current) previewAudioRef.current.pause();
        if (onVoiceCreated && voiceProfile) {
            onVoiceCreated(voiceProfile);
        }
    };

    const handleTryNewSample = async () => {
        // Delete current profile and reset to upload form
        if (previewAudioRef.current) previewAudioRef.current.pause();
        setShowPreview(false);
        setPreviewUrl(null);
        setPreviewError(null);

        try {
            await voiceover.deleteMyVoice(teacherId);
        } catch (err) {
            console.warn('Could not delete voice during retry:', err);
        }

        setVoiceProfile(null);
        setAudioFile(null);
        setFileDuration(null);
        setDisplayName('');
        setConsentChecked(false);
    };

    const togglePreviewPlayback = () => {
        const audio = previewAudioRef.current;
        if (!audio) return;

        if (previewPlaying) {
            audio.pause();
            setPreviewPlaying(false);
        } else {
            audio.play();
            setPreviewPlaying(true);
            trackAudioEvent('audio_preview_played', { preset: activePreset });
        }
    };

    // Toggle source sample playback
    const toggleSourcePlayback = useCallback(() => {
        const audio = sourceAudioRef.current;
        if (!audio) return;
        // Pause preview if playing
        if (previewAudioRef.current && !previewAudioRef.current.paused) {
            previewAudioRef.current.pause();
            setPreviewPlaying(false);
        }
        if (audio.paused) {
            audio.play();
            setSourcePlaying(true);
        } else {
            audio.pause();
            setSourcePlaying(false);
        }
    }, []);

    // Attach ended listener for source audio
    useEffect(() => {
        const audio = sourceAudioRef.current;
        if (!audio) return;
        const handleEnded = () => setSourcePlaying(false);
        audio.addEventListener('ended', handleEnded);
        return () => audio.removeEventListener('ended', handleEnded);
    }, [sourceSampleUrl]);

    // Attach ended listener for preview audio
    useEffect(() => {
        const audio = previewAudioRef.current;
        if (!audio) return;
        const handleEnded = () => setPreviewPlaying(false);
        audio.addEventListener('ended', handleEnded);
        return () => audio.removeEventListener('ended', handleEnded);
    }, [previewUrl]);

    const formatCreatedDate = (dateStr) => {
        if (!dateStr) return null;
        try {
            const d = new Date(dateStr);
            const now = new Date();
            const diffMs = now - d;
            const diffDays = Math.floor(diffMs / 86400000);
            if (diffDays === 0) return 'Created today';
            if (diffDays === 1) return 'Created yesterday';
            return `Created ${d.toLocaleDateString()}`;
        } catch {
            return null;
        }
    };

    const getDurationLabel = () => {
        if (!fileDuration) return null;
        const m = Math.floor(fileDuration / 60);
        const s = fileDuration % 60;
        if (m === 0) return `~${s}s`;
        return `~${m}m ${s}s`;
    };

    const getDurationQuality = () => {
        if (!fileDuration) return null;
        if (fileDuration >= 90) return 'good';     // 90-120s recommended
        if (fileDuration >= 60) return 'ok';        // 60-89s acceptable
        if (fileDuration >= 20) return 'marginal';  // 20-59s allowed but degraded
        return 'short';                              // <20s rejected
    };

    // Block creation if we know sample is too short
    const isSampleTooShort = fileDuration !== null && fileDuration < 20;

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                onClick={(e) => e.target === e.currentTarget && onClose()}
            >
                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 20 }}
                    className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
                >
                    {/* Header */}
                    <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Mic size={20} className="text-amber-500" />
                            <h2 className="text-lg font-semibold text-[#1d1d1f]">Voice Profile</h2>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                        >
                            <X size={18} />
                        </button>
                    </div>

                    {/* Body */}
                    <div className="p-6">
                        {loading ? (
                            <div className="flex flex-col items-center py-8 text-slate-400">
                                <Loader2 size={24} className="animate-spin mb-2" />
                                <p className="text-sm">Checking your voice profile…</p>
                            </div>

                        ) : creating ? (
                            /* === CREATING — PHASED PROGRESS === */
                            <div className="flex flex-col items-center py-10">
                                <div className="relative mb-4">
                                    <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center">
                                        <Mic size={28} className="text-amber-500 animate-pulse" />
                                    </div>
                                </div>
                                <p className="font-medium text-[#1d1d1f] text-center">
                                    {CREATION_PHASES[creationPhase]}
                                </p>
                                <p className="text-sm text-slate-400 mt-2 text-center">
                                    This usually takes 15–30 seconds.
                                </p>
                                <div className="flex gap-1.5 mt-4">
                                    {CREATION_PHASES.map((_, i) => (
                                        <div
                                            key={i}
                                            className={`w-2 h-2 rounded-full transition-all duration-500 ${
                                                i <= creationPhase ? 'bg-amber-500' : 'bg-slate-200'
                                            }`}
                                        />
                                    ))}
                                </div>
                            </div>

                        ) : showPreview && voiceProfile ? (
                            /* === PREVIEW — HEAR YOUR VOICE === */
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="space-y-4"
                            >
                                <div className="bg-green-50 rounded-xl p-4 text-center">
                                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-2">
                                        <Check size={20} className="text-green-600" />
                                    </div>
                                    <p className="font-semibold text-green-700 text-sm">
                                        Voice profile created!
                                    </p>
                                    <p className="text-xs text-green-600 mt-1">
                                        "{voiceProfile.display_name}" is saved.
                                    </p>
                                </div>

                                {/* Source Sample Player — for comparison */}
                                {sourceSampleUrl && (
                                    <div className="bg-blue-50 rounded-xl p-4">
                                        <p className="text-sm font-medium text-blue-700 mb-3 flex items-center gap-1.5">
                                            <Mic size={14} className="text-blue-500" />
                                            Your Uploaded Voice
                                        </p>
                                        <div className="flex items-center gap-3">
                                            <audio ref={sourceAudioRef} src={sourceSampleUrl} preload="auto" />
                                            <button
                                                onClick={toggleSourcePlayback}
                                                className="w-10 h-10 flex items-center justify-center bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-colors flex-shrink-0"
                                            >
                                                {sourcePlaying ? <Pause size={16} /> : <Play size={16} className="ml-0.5" />}
                                            </button>
                                            <div className="flex-1">
                                                <p className="text-sm text-blue-600">
                                                    {sourcePlaying ? 'Playing source…' : 'Tap play to hear your original'}
                                                </p>
                                                <p className="text-xs text-blue-400 mt-0.5">
                                                    Compare this against the preview below
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Preview Player */}
                                <div className="bg-slate-50 rounded-xl p-4">
                                    <p className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-1.5">
                                        <Volume2 size={14} className="text-amber-500" />
                                        Here's how your teaching voice sounds:
                                    </p>

                                    {previewLoading ? (
                                        <div className="flex items-center gap-2 text-slate-400 py-3 justify-center">
                                            <Loader2 size={16} className="animate-spin" />
                                            <span className="text-sm">{presetUpdating ? 'Applying new settings…' : 'Generating preview…'}</span>
                                        </div>
                                    ) : previewUrl ? (
                                        <div className="flex items-center gap-3">
                                            <audio ref={previewAudioRef} src={previewUrl} preload="auto" />
                                            <button
                                                onClick={togglePreviewPlayback}
                                                className="w-10 h-10 flex items-center justify-center bg-amber-500 text-white rounded-full hover:bg-amber-600 transition-colors flex-shrink-0"
                                            >
                                                {previewPlaying ? <Pause size={16} /> : <Play size={16} className="ml-0.5" />}
                                            </button>
                                            <div className="flex-1">
                                                <p className="text-sm text-slate-600">
                                                    {previewPlaying ? 'Playing preview…' : 'Tap play to listen'}
                                                </p>
                                                <p className="text-xs text-slate-400 mt-0.5">
                                                    Short sample using your cloned voice
                                                </p>
                                            </div>
                                        </div>
                                    ) : previewError ? (
                                        <div className="space-y-2">
                                            <p className="text-sm text-slate-500">{previewError}</p>
                                            <button
                                                onClick={() => generatePreview()}
                                                className="text-xs text-amber-600 font-medium hover:text-amber-700 flex items-center gap-1"
                                            >
                                                <RotateCcw size={12} />
                                                Try preview again
                                            </button>
                                        </div>
                                    ) : null}
                                </div>

                                {/* Fidelity Preset Selector */}
                                <div className="bg-slate-50 rounded-xl p-3.5">
                                    <p className="text-xs font-medium text-slate-600 mb-2">Voice style:</p>
                                    <div className="space-y-2">
                                        {availablePresets.map(p => (
                                            <button
                                                key={p.id}
                                                onClick={() => handlePresetChange(p.id)}
                                                disabled={presetUpdating || previewLoading || !p.available}
                                                className={`w-full px-3 py-2.5 rounded-lg text-left transition-all text-xs border flex items-center gap-2 ${
                                                    activePreset === p.id
                                                        ? 'bg-amber-50 border-amber-400 text-amber-700'
                                                        : p.available
                                                            ? 'bg-white border-slate-200 text-slate-600 hover:border-amber-300 hover:bg-amber-50/50'
                                                            : 'bg-slate-50 border-slate-100 text-slate-400 cursor-not-allowed'
                                                } disabled:opacity-50`}
                                            >
                                                <div className="flex-1 min-w-0">
                                                    <span className="block font-medium">{p.label}</span>
                                                    <span className="block text-[10px] font-normal text-slate-400 mt-0.5">
                                                        {p.best_for || p.description || ''}
                                                    </span>
                                                </div>
                                                {!p.available && (
                                                    <Lock size={12} className="text-slate-300 flex-shrink-0" />
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                    {/* Phase 3A — locked preset nudge (only if any presets are locked) */}
                                    {availablePresets.some(p => !p.available) && (
                                        <div className="mt-2">
                                            <UpgradeNudge
                                                nudgeType="locked_preset"
                                                featureArea="audio"
                                                compact={true}
                                                onUpgrade={() => {
                                                    // Open pricing in new tab since we're in a modal
                                                    window.open('/pricing?source=nudge_locked_preset', '_blank');
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>

                                {/* Preview Actions */}
                                {onVoiceCreated && (
                                    <button
                                        onClick={handleContinue}
                                        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#FFC107] text-black rounded-xl font-medium text-sm hover:bg-[#e6ad00] transition-all"
                                    >
                                        <Check size={16} />
                                        {previewUrl ? 'Sounds Good — Continue' : 'Continue to Voiceover'}
                                        <ArrowRight size={16} />
                                    </button>
                                )}

                                <button
                                    onClick={handleTryNewSample}
                                    className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm text-slate-500 hover:text-slate-700 hover:bg-slate-50 rounded-xl transition-colors"
                                >
                                    <RotateCcw size={14} />
                                    Try a New Sample
                                </button>
                            </motion.div>

                        ) : voiceProfile ? (
                            /* === HAS VOICE === */
                            <div className="space-y-4">
                                <div className="bg-green-50 rounded-xl p-4 text-center">
                                    <Check size={32} className="text-green-500 mx-auto mb-2" />
                                    <p className="font-medium text-green-700">Voice Profile Active</p>
                                    <p className="text-sm text-green-600 mt-1">
                                        {voiceProfile.display_name}
                                    </p>
                                    {formatCreatedDate(voiceProfile.created_at) && (
                                        <p className="text-xs text-green-500 mt-1">
                                            {formatCreatedDate(voiceProfile.created_at)}
                                        </p>
                                    )}
                                </div>

                                {/* Source Sample Player */}
                                {sourceSampleUrl && (
                                    <div className="bg-blue-50 rounded-xl p-3.5">
                                        <p className="text-xs font-medium text-blue-600 mb-2 flex items-center gap-1.5">
                                            <Mic size={12} className="text-blue-500" />
                                            Your Original Sample
                                        </p>
                                        <div className="flex items-center gap-3">
                                            <audio ref={sourceAudioRef} src={sourceSampleUrl} preload="auto" />
                                            <button
                                                onClick={toggleSourcePlayback}
                                                className="w-8 h-8 flex items-center justify-center bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-colors flex-shrink-0"
                                            >
                                                {sourcePlaying ? <Pause size={14} /> : <Play size={14} className="ml-0.5" />}
                                            </button>
                                            <p className="text-xs text-blue-500">
                                                {sourcePlaying ? 'Playing…' : 'Tap to listen'}
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {onVoiceCreated && (
                                    <button
                                        onClick={handleContinue}
                                        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#FFC107] text-black rounded-xl font-medium text-sm hover:bg-[#e6ad00] transition-all"
                                    >
                                        Continue to Voiceover
                                        <ArrowRight size={16} />
                                    </button>
                                )}

                                {/* Delete / Reset — always visible */}
                                {confirmingDelete ? (
                                    /* Inline confirmation */
                                    <motion.div
                                        initial={{ opacity: 0, y: 4 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-3"
                                    >
                                        <div className="flex items-start gap-2">
                                            <AlertTriangle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
                                            <div>
                                                <p className="text-sm font-medium text-red-700">
                                                    Remove this voice profile?
                                                </p>
                                                <p className="text-xs text-red-600 mt-1 leading-relaxed">
                                                    You'll need to upload a new voice sample before generating any new voiceovers.
                                                    Existing lesson audio you've already created will not be deleted.
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={handleDelete}
                                                disabled={deleting}
                                                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
                                            >
                                                {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                                                {deleting ? 'Removing…' : 'Yes, Remove Voice'}
                                            </button>
                                            <button
                                                onClick={() => setConfirmingDelete(false)}
                                                disabled={deleting}
                                                className="flex-1 px-3 py-2 bg-white border border-slate-200 text-slate-600 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors disabled:opacity-50"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </motion.div>
                                ) : (
                                    /* Reset prompt — above the fold, clear and friendly */
                                    <div className="bg-slate-50 rounded-xl p-3.5">
                                        <p className="text-xs text-slate-500 mb-2">
                                            Not happy with how it sounds? Remove this voice and create a new one.
                                        </p>
                                        <button
                                            onClick={() => setConfirmingDelete(true)}
                                            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm text-red-600 bg-red-50 hover:bg-red-100 rounded-lg font-medium transition-colors"
                                        >
                                            <Trash2 size={15} />
                                            Remove Voice & Start Over
                                        </button>
                                    </div>
                                )}

                                {/* Error */}
                                {error && (
                                    <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                                        <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
                                        <span>{error}</span>
                                    </div>
                                )}
                            </div>

                        ) : (
                            /* === NO VOICE — SETUP FORM === */
                            <div className="space-y-4">
                                {/* Guidance */}
                                <div className="bg-amber-50/60 rounded-xl p-3.5 space-y-1.5">
                                    <p className="text-sm font-medium text-slate-700">
                                        Record your teaching voice
                                    </p>
                                    <ul className="text-xs text-slate-600 space-y-1 list-none">
                                        <li className="flex items-start gap-1.5">
                                            <span className="text-amber-500 mt-0.5">•</span>
                                            <strong>90–120 seconds</strong> of natural speech — like you would in class
                                        </li>
                                        <li className="flex items-start gap-1.5">
                                            <span className="text-amber-500 mt-0.5">•</span>
                                            <strong>One speaker only</strong> — no background voices or conversations
                                        </li>
                                        <li className="flex items-start gap-1.5">
                                            <span className="text-amber-500 mt-0.5">•</span>
                                            Quiet room — <strong>no echo, fans, or music</strong>
                                        </li>
                                        <li className="flex items-start gap-1.5">
                                            <span className="text-amber-500 mt-0.5">•</span>
                                            Natural teaching pace — <strong>no whispering or shouting</strong>
                                        </li>
                                        <li className="flex items-start gap-1.5">
                                            <span className="text-amber-500 mt-0.5">•</span>
                                            Steady volume — avoid dramatic ups and downs
                                        </li>
                                    </ul>
                                </div>

                                {/* File Upload */}
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                        Voice Sample
                                    </label>
                                    <label className="flex items-center justify-center gap-2 w-full px-4 py-8 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:border-amber-400 hover:bg-amber-50/30 transition-colors">
                                        <input
                                            type="file"
                                            accept=".mp3,.wav,.m4a,audio/*"
                                            onChange={handleFileChange}
                                            className="hidden"
                                        />
                                        {audioFile ? (
                                            <div className="text-center">
                                                <Check size={20} className="text-green-500 mx-auto mb-1" />
                                                <p className="text-sm font-medium text-slate-700 truncate max-w-[200px]">
                                                    {audioFile.name}
                                                </p>
                                                <div className="flex items-center justify-center gap-2 mt-1">
                                                    <span className="text-xs text-slate-400">
                                                        {(audioFile.size / 1024 / 1024).toFixed(1)} MB
                                                    </span>
                                                    {fileDuration && (
                                                        <>
                                                            <span className="text-xs text-slate-300">·</span>
                                                            <span className={`text-xs font-medium ${
                                                                getDurationQuality() === 'good' ? 'text-green-600' :
                                                                getDurationQuality() === 'ok' ? 'text-green-600' :
                                                                getDurationQuality() === 'marginal' ? 'text-amber-600' :
                                                                'text-red-500'
                                                            }`}>
                                                                {getDurationLabel()}
                                                                {getDurationQuality() === 'good' && ' ✓ Recommended'}
                                                                {getDurationQuality() === 'ok' && ' ✓ Acceptable'}
                                                                {getDurationQuality() === 'marginal' && ' — may sound less natural'}
                                                                {getDurationQuality() === 'short' && ' — too short (20s minimum)'}
                                                            </span>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="text-center">
                                                <Upload size={24} className="text-slate-400 mx-auto mb-1" />
                                                <p className="text-sm text-slate-500">
                                                    Drop audio file or click to browse
                                                </p>
                                                <p className="text-xs text-slate-400">
                                                    MP3, WAV, or M4A · 90–120 seconds recommended
                                                </p>
                                            </div>
                                        )}
                                    </label>
                                    <div className="flex items-center gap-1.5 mt-2 text-xs text-slate-400">
                                        <Lightbulb size={12} className="text-amber-400 flex-shrink-0" />
                                        <span>The longer and clearer your sample, the more natural your voice will sound.</span>
                                    </div>
                                </div>

                                {/* Display Name (optional) */}
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                        Voice Name <span className="text-slate-400 font-normal">(optional)</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={displayName}
                                        onChange={(e) => setDisplayName(e.target.value)}
                                        placeholder={getDefaultDisplayName()}
                                        className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
                                    />
                                </div>

                                {/* Consent */}
                                <label className="flex items-start gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={consentChecked}
                                        onChange={(e) => setConsentChecked(e.target.checked)}
                                        className="mt-0.5 w-4 h-4 text-amber-500 rounded border-slate-300 focus:ring-amber-400"
                                    />
                                    <span className="text-xs text-slate-600 leading-relaxed">
                                        I confirm this is a recording of <strong>my own voice</strong> and I give
                                        Saige permission to create my AI teaching voice from it.
                                    </span>
                                </label>

                                {/* Error */}
                                {error && (
                                    <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                                        <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
                                        <span>{error}</span>
                                    </div>
                                )}

                                {/* Short sample warning — honest about quality trade-off */}
                                {getDurationQuality() === 'marginal' && (
                                    <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700 leading-relaxed">
                                        <AlertTriangle size={14} className="flex-shrink-0 mt-0.5 text-amber-500" />
                                        <div>
                                            <p className="font-medium">This sample is short</p>
                                            <p className="mt-0.5 text-amber-600">
                                                Shorter samples can produce less accurate cloning — the voice may sound more generic
                                                and less like you. For best results, use a 90–120 second recording.
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {/* Create Button */}
                                <button
                                    onClick={handleCreate}
                                    disabled={!audioFile || !consentChecked || creating || isSampleTooShort}
                                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#FFC107] text-black rounded-xl font-medium text-sm hover:bg-[#e6ad00] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <Mic size={16} />
                                    {isSampleTooShort ? 'Sample Too Short (20s minimum)' : getDurationQuality() === 'marginal' ? 'Create Anyway' : 'Create Voice Profile'}
                                </button>
                            </div>
                        )}
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

export default VoiceSetupModal;
